import { create } from "zustand";
import type { User } from "../lib/api/types";

/**
 * Real session state, backed by console-server's HttpOnly auth cookies
 * (see console/cmd/console-server/cookies.go). This store does NOT hold
 * a token -- the JWT lives only in the HttpOnly cookie, inaccessible to
 * JS by design (XSS mitigation). This store just tracks "who does the
 * browser believe it's logged in as right now," for UI purposes (nav
 * bar, route guards, logout button) -- it is not itself a source of
 * authorization; the backend re-validates the cookie on every request.
 *
 * Not persisted to localStorage: on a fresh page load this starts as
 * "unknown" (`initialized: false`) until something (a bootstrap check or
 * a successful login/register call) sets it. There is deliberately no
 * "am I logged in" probe wired into app startup yet in this pass --
 * routes that need a user call useAuthStore's setters directly after
 * login/register/logout, and 401 responses (see lib/api/client.ts)
 * clear this store so stale UI state doesn't linger after a session
 * expires server-side.
 */
interface AuthState {
  user: User | null;
  initialized: boolean;
  setUser: (user: User) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  initialized: false,
  setUser: (user) => set({ user, initialized: true }),
  clear: () => set({ user: null, initialized: true }),
}));
